import dynamic from 'next/dynamic';
import {Component} from 'react';

import PortalLayout from '../../components/portal/layout/portal-layout.component';
import {lazyInject} from '../../context/module';
import {SettingsService} from '../../services/settings.service';

// Both branches are client-only (Inversify-injected services, RxJS
// subscriptions). Lazy-load both with ssr:false so the legacy and v3
// experiences ship in separate chunks and the SSG HTML stays empty
// until hydration picks the right one.
const PortalComponent = dynamic(() =>
    import('../../components/portal/portal.component').then(m => ({default: m.default})),
    {ssr: false},
);

const OverviewComponent = dynamic(() =>
    import('../../components/portal/overview/overview.component').then(m => ({
        default: m.OverviewComponent,
    })),
    {ssr: false},
);

// _app.tsx's `init()` bails out when `pageProps.url` is falsy. The portal
// pages need that initialization to fire on direct navigation, so stub
// the field via `getStaticProps` — matches the other portal pages.
export async function getStaticProps() {
    return {props: {url: '/portal'}};
}

interface State {
    /**
     * Tri-state: `undefined` while settings load, then `true`/`false`
     * once `SettingsService.featureFlags` emits. Without the
     * tri-state the legacy `<PortalComponent>` would render briefly
     * for v3 users on every navigation while flags hydrate.
     */
    portalV3Enabled: boolean | undefined;
}

/**
 * `/portal` route shell. Branches on the backend `portalV3Enabled`
 * feature flag:
 *
 *   - flag ON  → `<OverviewComponent>` (Phase 4 dashboard) inside the
 *                shared `<PortalLayout>` shell.
 *   - flag OFF → the legacy `<PortalComponent>` (today's My Account
 *                page) untouched.
 *
 * Phase 5 collapses this to v3-only.
 */
export default class PortalIndexPage extends Component<{}, State> {
    @lazyInject(SettingsService) private settingsService!: SettingsService;

    state: State = {portalV3Enabled: undefined};

    componentDidMount(): void {
        // featureFlags is a ReplaySubject(1) — fires immediately if
        // settings already loaded (e.g. cross-page nav), otherwise
        // waits for the first /v1/api/settings response.
        this.settingsService?.featureFlags?.subscribe({
            next: (ff) => this.setState({portalV3Enabled: ff?.portalV3Enabled === true}),
        });
    }

    render() {
        const {portalV3Enabled} = this.state;
        if (portalV3Enabled === undefined) {
            // Render the shell with an empty content slot to avoid a
            // legacy/v3 flicker. The shell itself has a skeleton-ish
            // sidebar so the user isn't staring at a blank viewport.
            return <PortalLayout activePath="/portal"><div /></PortalLayout>;
        }
        if (portalV3Enabled) {
            return (
                <PortalLayout
                    activePath="/portal"
                    breadcrumbs={[{label: 'Portal'}, {label: 'Overview'}]}
                >
                    <OverviewComponent />
                </PortalLayout>
            );
        }
        return <PortalComponent />;
    }
}
